import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import {
  type AdAnalysisDecision,
  calendarWeekRange,
  dashboardDateQuery,
  dashboardDateSelectionKey,
  DEFAULT_DASHBOARD_DATE_SELECTION,
  type DashboardDateSelection,
  type MarketplaceId,
  type CampaignActionApprovalBody,
  type CampaignActionAnalysisDetail,
  type CampaignActionPlan,
  type CampaignActionPlanListResponse,
  type CampaignActionStageBody,
  routes,
} from "@nova-ai/shared"
import type { CampaignFamily } from "@nova-ai/shared/campaign-family"

import { apiFetch } from "../lib/api"
import { keepPreviousForMarketplace } from "./marketplace-query"

export type { DashboardDateSelection }

export type DashboardChartBucket = {
  key: string
  gmv: number
  orderCount: number
}

/** The Magento-book KPI block (current or previous window). */
type DashboardKpis = {
  gmv: number
  averageBasket: number
  /** Average quantity of items per order over the window (0 when empty). */
  itemsPerBasket: number
  orderCount: number
}

type DashboardResponse = {
  marketplace: MarketplaceId
  selection: DashboardDateSelection
  selectionLabel: string
  comparisonLabel: string
  currency: string | null
  windowStart: string
  windowEnd: string
  comparisonWindowStart: string
  comparisonWindowEnd: string
  lastSyncedAt: string | null
  /** Seller storefront/back-office home on the marketplace's platform, or null
   *  when the marketplace declares none. Drives the dashboard "Open store" link. */
  storeUrl: string | null
  kpis: DashboardKpis
  previous: DashboardKpis
  chart: {
    granularity: "day" | "month"
    buckets: DashboardChartBucket[]
  }
  orders: {
    /** Magento-canceled orders created inside the selected window. */
    canceled: number
    /** Canceled share of ALL the window's Magento orders, in percent. */
    canceledPct: number
  }
}

const dashboardKey = (marketplace: MarketplaceId, selection: DashboardDateSelection) =>
  ["dashboard", marketplace, ...dashboardDateSelectionKey(selection)] as const

export function useMarketplaces() {
  return useQuery({
    queryKey: ["marketplaces"] as const,
    queryFn: async () => {
      const result = await apiFetch<{ marketplaces: MarketplaceId[] }>(routes.marketplaces.list())
      if (!result.ok) {
        throw new Error(result.error.message)
      }
      return result.value.marketplaces
    },
  })
}

export type MarketplaceSummary = {
  marketplace: MarketplaceId
  selection: DashboardDateSelection
  selectionLabel: string
  comparisonLabel: string
  chartGranularity: "day" | "month"
  currency: string | null
  lastSyncedAt: string | null
  gmv: number
  orderCount: number
  awaitingShipment: number
  awaitingAcceptance: number
  lateOrders: number
  returnsInProgress: number
  canceledOrders: number
  /** Per-bucket GMV over the window, keyed like the chart buckets so the time
   *  axis is labeled from the server's window, not the client clock. */
  spark: { key: string; gmv: number }[]
  /** GMV and order count over the immediately-preceding window of equal length,
   *  for the card's period-over-period trend. */
  previous: { gmv: number; orderCount: number }
}

export function useMarketplaceSummaries(
  selection: DashboardDateSelection = DEFAULT_DASHBOARD_DATE_SELECTION
) {
  return useQuery({
    queryKey: ["marketplace-summaries", ...dashboardDateSelectionKey(selection)] as const,
    // The configured/reporting counts do not become zero while another period
    // is loading. Keep the last confirmed summaries until the new read settles.
    placeholderData: keepPreviousData,
    queryFn: async () => {
      const result = await apiFetch<{ summaries: MarketplaceSummary[] }>(
        routes.marketplaces.summary(),
        { query: dashboardDateQuery(selection) }
      )
      if (!result.ok) {
        throw new Error(result.error.message)
      }
      return result.value.summaries
    },
  })
}

export function useDashboard(marketplace: MarketplaceId, selection: DashboardDateSelection) {
  return useQuery({
    queryKey: dashboardKey(marketplace, selection),
    enabled: !!marketplace,
    // Keep the previous period's data on screen while a new period loads, so
    // switching 7d/14d/30d doesn't blank the dashboard (and its header) for a
    // beat — that flash caused a visible layout shift.
    placeholderData: keepPreviousData,
    queryFn: async () => {
      const result = await apiFetch<DashboardResponse>(
        routes.marketplaces.dashboard({ marketplace }),
        { query: dashboardDateQuery(selection) }
      )
      if (!result.ok) {
        throw new Error(result.error.message)
      }
      return result.value
    },
  })
}

/** What the latest completed analysis decided about one product's bid, beside
 *  the bid the marketplace is actually charging.
 *
 *  The two can disagree, and that gap is the point: `action` is a
 *  RECOMMENDATION, and somebody still has to set it in the advertiser portal.
 *  `applied` says whether that has happened — and is null, not false, whenever
 *  the question is unanswerable (an action that moves no bid, no live bid to
 *  compare, or a bid older than the analysis itself). */
export type ProductBidReview = {
  campaignId: string
  campaignName: string | null
  /** The campaign's live bid, from the latest report day that had clicks. Null
   *  when no such day exists. */
  bid: number | null
  /** Report day `bid` comes from (yyyy-mm-dd); null with a null `bid`. */
  bidAsOf: string | null
  /** The day the analysis completed (yyyy-mm-dd). */
  reviewDate: string
  action: AdAnalysisDecision
  /** The bid the analysis said to move to; null for decisions that move none. */
  recommendedBid: number | null
  applied: boolean | null
  /** The analysis's own words. Always present. */
  reason: string
  analysis?: CampaignAnalysisDetail
}

export type CampaignAnalysisDetail = CampaignActionAnalysisDetail

/** The per-unit margin the row's economics rest on, with the inputs behind it so
 *  the cell can show its own arithmetic. Null on the row when no fact day in the
 *  window carried a usable snapshot — a placeholder catalog cost counts as
 *  unusable. */
export type CampaignMargin = {
  /** The fact day the snapshot came from. */
  asOf: string
  /** VAT-INCLUSIVE catalog price. The margin below is net of VAT; this is the
   *  gross figure the commission is charged on. */
  unitPrice: number | null
  /** The same price with VAT removed — what `breakevenRoas` is built on, since
   *  the ROAS it is compared against is net of VAT too. */
  netUnitPrice: number | null
  unitCost: number
  /** Fee-aware, net-of-VAT per-unit margin. */
  realIncome: number
}

/** One metric against the same metric in the window before, printed UNDER the
 *  number itself rather than in a column of its own.
 *
 *  `changePct` is null when the previous value was zero: a rise from nothing is
 *  not a percentage. The previous value travels so the hover can state the pair. */
export type CampaignMetricChange = { previous: number; changePct: number | null }

/** Whether the window bought enough evidence to call a line a loser. Present
 *  ONLY on rows below break-even — a line that is already earning has nothing to
 *  prove, and muting its figures would hide the winners. */
export type CampaignDataGate = { met: boolean; spend: number; required: number }

/** One advertised product over the card's own window (#482). Every decision is
 *  read from two pairs — ROAS against `breakevenRoas`, and the bid against
 *  `breakevenBid` — and both halves of each pair are facts about the product's
 *  economics, never advice.
 *
 *  `targetRoas` is the one figure with no column: it is `breakevenRoas` divided
 *  by a config constant, so printing it would say nothing the cell beside it does
 *  not. The ROAS cell colours itself against it instead. */
export type CampaignTableRow = {
  sku: string
  campaignId: string | null
  campaignName: string | null
  /** Which Redcare programme the campaign belongs to — the family picker filters
   *  on it. Null when the campaign name does not say; those rows get their own
   *  "Other" tab rather than being dropped. */
  campaignFamily: CampaignFamily | null
  name: string | null
  imageUrl: string | null
  productUrl: string | null
  adSpend: number
  impressions: number
  clicks: number
  ctrPct: number | null
  cvrPct: number | null
  roas: number | null
  breakevenRoas: number | null
  targetRoas: number | null
  margin: CampaignMargin | null
  /** The bid charged inside this window — the latest day in it that had clicks.
   *  Window-scoped so it carries a change like every other figure; the review's
   *  mark beside it stays unwindowed. */
  bid: number | null
  /** Period-INDEPENDENT: a bid and a standing recommendation are the product's
   *  state now, so this one field ignores the card's period picker. */
  bidReview: ProductBidReview | null
  /** Each window-scoped metric against the window before. Null on a metric the
   *  previous window cannot state — most often because the product was not
   *  advertised then, which is not the same as "it did not move". */
  changes: {
    adSpend: CampaignMetricChange | null
    roas: CampaignMetricChange | null
    bid: CampaignMetricChange | null
    cvrPct: CampaignMetricChange | null
    clicks: CampaignMetricChange | null
    impressions: CampaignMetricChange | null
    ctrPct: CampaignMetricChange | null
  }
  dataGate: CampaignDataGate | null
}

/** One day of the window — the daily-ROAS chart's series (#577). Rides on the
 *  campaign-table read rather than a second one, so the chart and the table
 *  beneath it cannot disagree about the window or about what ROAS means: both are
 *  net of VAT, stripped per fact.
 *
 *  A day nothing was spent on is ABSENT rather than zero, and the chart leaves a
 *  hole — a zero would claim the money went out and nothing came back. */
export type CampaignDailyPoint = {
  date: string
  spend: number
  /** Net-of-VAT ad revenue; null when a revenue-bearing fact carried no rate to
   *  strip with. */
  netAdRevenue: number | null
  /** `netAdRevenue / spend`; null when nothing was spent or revenue is unknown. */
  roas: number | null
}

type CampaignTableResponse = {
  marketplace: MarketplaceId
  window: { from: string; to: string }
  previousWindow: { from: string; to: string }
  /** False when the window holds no advertiser-report rows at all — an unimported
   *  window and a window nobody advertised in are different facts. */
  reported: boolean
  rows: CampaignTableRow[]
  daily: CampaignDailyPoint[]
  campaignActionsEnabled: boolean
  campaignActionsPreview: boolean
  campaignActionTimeZone: string
  recommendedNewCampaigns: CampaignCandidateRow[]
}

export type CampaignCandidateRow = {
  sku: string
  name: string | null
  ean: string | null
  imageUrl: string | null
  productUrl: string | null
  analysis: CampaignAnalysisDetail
}

/** The card's own period. It does NOT follow the page picker (#482): the table
 *  is judged weekly, against the Monday review, and the page's window answers a
 *  different question. */
export type CampaignPeriod =
  | { kind: "last-week" }
  | { kind: "last-30d" }
  | { kind: "custom"; from: string; to: string }

export const DEFAULT_CAMPAIGN_PERIOD: CampaignPeriod = { kind: "last-week" }

/** The card's period as the API's date query. Weeks travel as a custom from/to —
 *  the shared `calendarWeekRange` owns which days those are, so the component
 *  never counts Mondays itself. */
export function campaignPeriodQuery(
  period: CampaignPeriod,
  now: Date = new Date()
): Record<string, string> {
  switch (period.kind) {
    case "last-week":
      return calendarWeekRange(now, 1)
    case "last-30d":
      return { period: "30d" }
    case "custom":
      return { from: period.from, to: period.to }
  }
}

/** The campaign table for one marketplace over the CARD's window. A separate
 *  query from {@link useDashboard} (offer-health pattern) so it loads on its own
 *  and keeps its own period. */
export function useCampaignTable(marketplace: MarketplaceId, period: CampaignPeriod) {
  const query = campaignPeriodQuery(period)
  return useQuery({
    queryKey: ["campaign-table", marketplace, query] as const,
    enabled: !!marketplace,
    // Period changes may keep this shop's table visible. A marketplace switch
    // must not render the previous shop's products under the new shop heading.
    placeholderData: keepPreviousForMarketplace(marketplace),
    queryFn: async () => {
      const result = await apiFetch<CampaignTableResponse>(
        routes.marketplaces.campaignTable({ marketplace }),
        { query }
      )
      if (!result.ok) {
        throw new Error(result.error.message)
      }
      return result.value
    },
  })
}

export function useCampaignActionPlans(
  marketplace: MarketplaceId,
  subject: CampaignActionPlan["subject"],
  pollingEnabled = true
) {
  return useQuery({
    queryKey: ["campaign-action-plans", marketplace, subject] as const,
    enabled: subject.productId !== "",
    // The drawer stays authoritative while open, even with no active plan. A
    // newly completed analysis must replace stale detail without a close/reopen
    // where actions are available. Disabled production still performs one read
    // for its analysis and durable history, but never polls this endpoint.
    refetchInterval: campaignActionPlanRefetchInterval(pollingEnabled),
    queryFn: async () => {
      const result = await apiFetch<CampaignActionPlanListResponse>(
        routes.marketplaces.campaignActionPlans({ marketplace }),
        {
          query: {
            subjectKind: subject.kind,
            productId: subject.productId,
            ...(subject.kind === "existing-campaign" ? { campaignId: subject.campaignId } : {}),
          },
        }
      )
      if (!result.ok) {
        throw new Error(result.error.message)
      }
      return result.value
    },
  })
}

export function campaignActionPlanRefetchInterval(pollingEnabled: boolean): 2_000 | false {
  return pollingEnabled ? 2_000 : false
}

export function useStageCampaignAction(marketplace: MarketplaceId) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (body: CampaignActionStageBody) => {
      const result = await apiFetch<CampaignActionPlan>(
        routes.marketplaces.campaignActionPlans({ marketplace }),
        { method: "POST", body }
      )
      if (!result.ok) {
        throw new Error(result.error.message)
      }
      return result.value
    },
    onSuccess: (plan) =>
      queryClient.invalidateQueries({
        queryKey: ["campaign-action-plans", marketplace, plan.subject],
      }),
  })
}

export function useApproveCampaignAction(marketplace: MarketplaceId) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (input: { planId: string; body: CampaignActionApprovalBody }) => {
      const result = await apiFetch<CampaignActionPlan>(
        routes.marketplaces.campaignActionPlanApproval({
          marketplace,
          planId: input.planId,
        }),
        { method: "POST", body: input.body }
      )
      if (!result.ok) {
        throw new Error(result.error.message)
      }
      return result.value
    },
    onSuccess: (plan) =>
      queryClient.invalidateQueries({
        queryKey: ["campaign-action-plans", marketplace, plan.subject],
      }),
  })
}
